import {
  Box,
  chakra,
  Flex,
  Heading,
  HStack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Panel } from '@gamma/ui/layout';
import { Modal } from '@gamma/ui/overlay';
import { ReactNode, useEffect, useState } from 'react';
import { i18n } from '../../../../shared/localization';
import { detailsProps } from '../../queries';
import { stringifyAlertType } from '@investigator/shared/utils';

import { ChatGPTToolTip } from '../../../entities/components';
import { SuricataAlertDetailsLayout } from '../SuricataAlertDetailsLayout';
import { SeverityScore } from '@investigator/shared/components';

export interface valueProps {
  alert_type?: string;
  alert_name?: string;
  category?: string;
  severity?: string;
  cve_reference?: string;
  alert_severity?: number;
  signature_id?: string;
  message?: string;
  description?: string;
  custom_severity?: number;
}

interface detailsPropsExtended extends Omit<detailsProps, 'input'> {
  input?: string;
}

interface AlertDetailsModalProps {
  value?: valueProps;
  children: ReactNode;
  isGPTEnabled?: boolean;
}

export const SuricataAlertDetailsModal = ({
  value,
  children,
  isGPTEnabled,
}: AlertDetailsModalProps) => {
  const [content, setContent] = useState(value);
  const { isOpen, onClose, onOpen } = useDisclosure();

  const suricataSummary =
    i18n.pages.alertDetails.suricata.summary.cards.alertSummary;

  useEffect(() => {
    if (value) {
      setContent(value);
    }
  }, [value]);

  const details: detailsPropsExtended[] = [
    {
      label: suricataSummary?.type,
      input: stringifyAlertType(content?.alert_type),
    },
    {
      label: suricataSummary.category,
      input: content?.category,
    },
    {
      label: suricataSummary.signatureId,
      input: content?.signature_id,
    },
    {
      label: suricataSummary.cve_reference,
      input: content?.cve_reference,
    },
    { label: suricataSummary.message, input: content?.message },
  ];

  const descriptionLabelDetails = {
    label: suricataSummary.description,
    input: content?.description,
    lineBreak: [true, true],
    truncate: false,
  };

  const scoreToShow = content?.alert_severity as number;

  return (
    <Modal
      title={suricataSummary.heading}
      data-testid="suricata-alert-details-modal"
      size={'xl'}
      isCentered={true}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      body={
        <Panel layerStyle="second">
          <Flex
            alignItems="center"
            mt={
              content?.alert_severity ||
              (content?.message && content?.alert_type)
                ? 3
                : ''
            }
            mb={3}
          >
            {true && (
              <Box mr={2}>
                <SeverityScore score={scoreToShow} />
              </Box>
            )}
            {content?.alert_name && content?.alert_type && (
              <Heading textStyle="h4" wordBreak="break-word" maxW={'50%'}>
                {content?.alert_name}
              </Heading>
            )}
          </Flex>
          <Flex>
            <Flex mr={4} flexDir="column" flex="1 1 66%">
              <HStack mb={2}>
                {isGPTEnabled && <ChatGPTToolTip />}
                <chakra.span>
                  {i18n.pages.integrations.chatGPT.description}:
                </chakra.span>
              </HStack>
              <Text textStyle="body-md">{descriptionLabelDetails?.input}</Text>
            </Flex>
            <Flex flexDir="column" flex="1 1 34%">
              {details?.map((detail, index) => {
                if (detail.label === suricataSummary.description) {
                  return null;
                }
                return (
                  <SuricataAlertDetailsLayout
                    isInModal={true}
                    key={index}
                    label={detail.label}
                    input={detail.input}
                    options={{
                      lineBreak: detail.lineBreak,
                      truncate: detail.truncate,
                    }}
                  />
                );
              })}
            </Flex>
          </Flex>
        </Panel>
      }
    >
      {children}
    </Modal>
  );
};
